'use client';

import { useEffect, useState } from 'react';

import { Cms } from '@bytel/product-wall';
import {
    Alignable,
    Box,
    BoxContent,
    Button,
    ButtonVariant,
    Column,
    Columns,
    Icon,
    IconName,
    IconSize,
    Image,
    Link,
    Price,
    PriceLevel,
    Row,
    Rows,
    Sticker,
    Tag,
    TagVariant,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TypographyAlign,
    VariantState,
    View,
} from '@bytel/trilogy-react';

import { HomepackProduct, HomepackProductOptions, StaticPackProduct } from '@app-types/home-pack';

import { NuggetsList } from '@components/roc-card';

import { sendProductSelectTracking } from '@home-pack/helpers';

import { ProductColorContainer } from './product-color-container';
import { ProductSizeContainer } from './product-size-container';

const MAX_COLUMN_SIZE = 12;

function getColumnNumber(length: number) {
    switch (length) {
        case 1:
        case 3:
        case 6:
        case 7:
            return 4;
        case 2:
        default:
            return 6;
    }
}

function checkIfProductAdded(
    hasMultipleSizes: boolean,
    hasMultipleColors: boolean,
    selectedProduct?: StaticPackProduct,
    localTemporarySelectedProduct?: StaticPackProduct,
) {
    return (
        (hasMultipleSizes ? selectedProduct?.details?.cm === localTemporarySelectedProduct?.details?.cm : true) &&
        (hasMultipleColors
            ? selectedProduct?.details?.colorCode === localTemporarySelectedProduct?.details?.colorCode
            : true) &&
        (selectedProduct && localTemporarySelectedProduct
            ? selectedProduct?.mainProduct?.name === localTemporarySelectedProduct?.mainProduct?.name
            : false)
    );
}

type ProductCardProps = {
    product: HomepackProduct;
    onSelectProduct: (mainProduct: HomepackProduct, details: HomepackProductOptions) => void;
    selectedProduct?: StaticPackProduct;
    withBorder?: boolean;
    hasProductWithFourNuggets?: boolean;
};

export function ProductCard({
    product,
    onSelectProduct,
    selectedProduct,
    withBorder = false,
    hasProductWithFourNuggets,
}: ProductCardProps) {
    const [localTemporarySelectedProduct, setLocalTemporarySelectedProduct] = useState<StaticPackProduct | null>(null);

    useEffect(() => {
        if (product) {
            setLocalTemporarySelectedProduct({
                mainProduct: product,
                details: product.options.find((size) => size.defaultProduct) ?? product.options[0] ?? null,
            });
        }
    }, [product]);

    const handleTemporarySelectedProduct = (mainProduct: HomepackProduct, details: HomepackProductOptions) => {
        setLocalTemporarySelectedProduct({
            mainProduct,
            details,
        });
    };

    const handleSelectProduct = () => {
        sendProductSelectTracking(product.name);
        if (localTemporarySelectedProduct?.mainProduct && localTemporarySelectedProduct?.details) {
            onSelectProduct(localTemporarySelectedProduct.mainProduct, localTemporarySelectedProduct?.details);
        }
    };

    if (!product) {
        return null;
    }

    const hasMultipleSizes = product.options.length > 1 && product.options.every((size) => size.cm);
    const hasMultipleColors = product.options.length > 1 && product.options.every((size) => size.colorCode);

    const isProductAdded = checkIfProductAdded(
        hasMultipleSizes,
        hasMultipleColors,
        selectedProduct,
        localTemporarySelectedProduct ?? undefined,
    );

    const stickerMobile = localTemporarySelectedProduct?.details?.stickerMobile;
    const sticker = localTemporarySelectedProduct?.details?.sticker;

    const copyRef = () => navigator.clipboard.writeText(localTemporarySelectedProduct?.details?.productReference || '');
    return (
        <View className='product-card is-fullheight fai-offer-card'>
            <Box active={withBorder} flat={withBorder} fullheight>
                <BoxContent>
                    <Rows className='has-text-centered' gap={3}>
                        <Row narrow className='header-row'>
                            <View className='is-flex is-spaced-between gap-medium'>
                                <div>
                                    {product.brandLogo && (
                                        <View className='brand-logo-container'>
                                            <Image alt={product.brand ?? ''} src={product.brandLogo} />
                                        </View>
                                    )}
                                    <Title level={TitleLevels.FOUR} typo={TypographyAlign.TEXT_LEFT}>
                                        <Cms content={product.name} />
                                    </Title>
                                </div>
                                {product.image && <img alt='' className='custom-max-height' src={product.image} />}
                            </View>
                        </Row>
                        <Row narrow className='sticker-row is-hidden-mobile'>
                            {sticker && <Sticker variant={VariantState.ACCENT} small label={sticker} />}
                        </Row>
                        <Row narrow className='sticker-row is-hidden-tablet'>
                            {(stickerMobile || sticker) && (
                                <Sticker variant={VariantState.ACCENT} small label={(stickerMobile ?? sticker)!} />
                            )}
                        </Row>
                        <Row narrow>
                            <Text className='is-flex is-centered is-aligned-center gap-medium'>
                                {localTemporarySelectedProduct?.details?.price !== undefined ? (
                                    <Price
                                        level={PriceLevel.FOUR}
                                        oldAmount={localTemporarySelectedProduct?.details?.priceStriked}
                                        amount={localTemporarySelectedProduct?.details?.price}
                                    />
                                ) : null}
                            </Text>
                            <Text className='price-description' marginless level={TextLevels.TWO}>
                                {localTemporarySelectedProduct?.details?.priceDescription}
                            </Text>
                        </Row>
                        {product.options.every((size) => size.cm) ? (
                            <Row className={`size-selector ${product.options.length > 2 ? 'two-lines' : ''}`}>
                                <Columns
                                    align={product.options.length === 1 ? Alignable.ALIGNED_CENTER : undefined}
                                    mobile
                                    multiline
                                    className='is-fullheight'
                                    gap={2}
                                >
                                    {product.options.map((data, index, array) => {
                                        const columnSize = getColumnNumber(array.length);
                                        const row = Math.floor((index * columnSize) / 12) + 1;

                                        const endIndex = (MAX_COLUMN_SIZE / columnSize) * row;
                                        const startIndex = endIndex - MAX_COLUMN_SIZE / columnSize;

                                        // Check if the current line contains any promotional products
                                        const isPromoRow =
                                            row !== 1 &&
                                            product.options.slice(startIndex, endIndex).some(({ promo }) => promo);

                                        return (
                                            <Column
                                                size={columnSize}
                                                key={`${product.id}-${data.cm}`}
                                                className={isPromoRow ? 'promo-space' : ''}
                                            >
                                                <ProductSizeContainer
                                                    product={product}
                                                    detail={data}
                                                    onSelectedProduct={handleTemporarySelectedProduct}
                                                    selectedProduct={localTemporarySelectedProduct || undefined}
                                                />
                                            </Column>
                                        );
                                    })}
                                </Columns>
                            </Row>
                        ) : null}
                        {product.options.every((size) => size.colorCode) ? (
                            <Row className={product.options.length > 2 ? 'two-lines' : ''}>
                                <Columns
                                    align={product.options.length === 1 ? Alignable.ALIGNED_CENTER : undefined}
                                    mobile
                                    multiline
                                    gap={2}
                                >
                                    {product.options.map((data, index, array) => {
                                        const columnSize = getColumnNumber(array.length);
                                        return (
                                            <Column size={columnSize} key={`${product.id}-${data.colorCode}`}>
                                                <ProductColorContainer
                                                    product={product}
                                                    detail={data}
                                                    onSelectedProduct={handleTemporarySelectedProduct}
                                                    selectedProduct={localTemporarySelectedProduct || undefined}
                                                />
                                            </Column>
                                        );
                                    })}
                                </Columns>
                            </Row>
                        ) : null}

                        <Row narrow className='cta-row'>
                            {isProductAdded ? (
                                <Tag
                                    variant={TagVariant.SUCCESS}
                                    iconName={IconName.CHECK_CIRCLE}
                                    label='Produit sélectionné'
                                />
                            ) : (
                                <Button fullwidth variant={ButtonVariant.PRIMARY} onClick={handleSelectProduct}>
                                    {product.ctaText ?? 'Sélectionner ce produit'}
                                </Button>
                            )}
                        </Row>

                        <Row narrow className={`roc-nuggets ${hasProductWithFourNuggets ? 'four-nuggets' : ''}`}>
                            {product.productDetails?.length > 0 && (
                                <NuggetsList
                                    nuggets={product.productDetails.map((product) => ({
                                        icon: product.iconeTrilogy,
                                        label: product.pepite,
                                    }))}
                                />
                            )}
                        </Row>

                        <Row narrow className='row-reference'>
                            {localTemporarySelectedProduct?.details?.productReference && (
                                <Text marginless level={TextLevels.THREE}>
                                    Référence&nbsp;: {localTemporarySelectedProduct?.details?.productReference}{' '}
                                    {/* icone temporaire, le temps que Trilogy ajoute la bonne icone - mi septembre ? */}
                                    <Icon
                                        className='cursor-pointer'
                                        onClick={copyRef}
                                        name={IconName.LINK_COPY}
                                        size={IconSize.SMALLER}
                                    />
                                </Text>
                            )}
                        </Row>
                        {product.externalLink && (
                            <Row narrow>
                                <Link href={product.externalLink.url} blank iconName={IconName.NEW_TABBED_PAGE}>
                                    {product.externalLink.text}
                                </Link>
                            </Row>
                        )}
                        <Row narrow>
                            <Text marginless level={TextLevels.THREE}>
                                *Prix de vente recommandé
                            </Text>
                        </Row>
                    </Rows>
                </BoxContent>
            </Box>
        </View>
    );
}
